import { Accordion, Box, useBreakpointValue, VStack } from '@chakra-ui/react';
import { Fragment, useMemo } from 'react';
import { type Fixture, gameStage } from '@/models/fixture';
import type { LiveMatch } from '@/models/liveMatch';
import { convertStageToString, formatFixtureDisplayLabel, sortFixturesChronological } from '../helpers';
import { FixtureMatchRow } from './FixtureMatchRow';
import { LiveMatchRow, liveMatchBracketLabel } from './LiveMatchRow';
import { ListSectionHeader } from './ListSectionHeader';
import { groupFixturesByMatchday } from '@/helpers/fixtures/matchdayGroups';

function fixtureBracketLabel(fixture: Fixture): string | undefined {
  const rawTitle = fixture.bracketLabel ?? fixture.matchId;
  return rawTitle ? formatFixtureDisplayLabel(rawTitle) : undefined;
}

export function FixtureListGrid(props: {
  fixtureList: Fixture[];
  svg?: boolean;
  liveMatches?: LiveMatch[];
  fontSize?: number;
  metaFontSize?: number;
  nameLines?: number;
  includeChevronColumn?: boolean;
}) {
  const responsiveFontSize = useBreakpointValue({ base: 13, sm: 15 });
  const responsiveMetaFontSize = useBreakpointValue({ base: 10, sm: 12 });
  const fontSize = props.fontSize ?? responsiveFontSize;
  const metaFontSize = props.metaFontSize ?? responsiveMetaFontSize;

  const liveByMatchId = useMemo(() => {
    const map = new Map<string, LiveMatch>();
    for (const match of props.liveMatches ?? []) {
      map.set(match.matchId, match);
    }
    return map;
  }, [props.liveMatches]);

  const useMatchdayLayout =
    props.fixtureList.length > 0 && props.fixtureList.every((f) => typeof f.matchday === 'number');

  const sortedForStage = (stage: gameStage) =>
    props.fixtureList
      .filter((x) => x.stage === stage)
      .sort(sortFixturesChronological);

  const renderFixtureBlock = (fixture: Fixture, keyPrefix: string, index: number) => {
    const key = fixture.matchId ?? `${keyPrefix}-${fixture.sortOrder ?? index}`;
    const liveMatch = fixture.matchId ? liveByMatchId.get(fixture.matchId) : undefined;

    if (liveMatch) {
      const bracketLabel = liveMatchBracketLabel(liveMatch) ?? fixtureBracketLabel(fixture);
      return (
        <LiveMatchRow
          key={key}
          liveMatch={liveMatch}
          svg={props.svg}
          showBracketLabel={Boolean(bracketLabel)}
          bracketLabel={bracketLabel}
        />
      );
    }

    const bracketLabel = fixtureBracketLabel(fixture);
    return (
      <FixtureMatchRow
        key={key}
        fixture={fixture}
        fontSize={fontSize}
        metaFontSize={metaFontSize}
        nameLines={props.nameLines}
        includeChevronColumn={props.includeChevronColumn}
        svg={props.svg}
        showBracketLabel={Boolean(bracketLabel)}
        bracketLabel={bracketLabel}
      />
    );
  };

  if (useMatchdayLayout) {
    const groups = groupFixturesByMatchday(props.fixtureList);
    return (
      <Box w='full' maxW='full'>
        {groups.map((g) => (
          <Fragment key={`md-${g.matchday}`}>
            <ListSectionHeader label={`Matchday ${g.matchday}`} />
            <VStack align='stretch' spacing={2} pt={2} pb={3}>
              {g.fixtures.map((fixture, i) => renderFixtureBlock(fixture, `md${g.matchday}`, i))}
            </VStack>
          </Fragment>
        ))}
      </Box>
    );
  }

  return (
    <Accordion w='full' maxW='full'>
      {[
        gameStage.groupStage,
        gameStage.roundOf32,
        gameStage.roundOf16,
        gameStage.quarterFinal,
        gameStage.semiFinal,
        gameStage.thirdPlayoff,
        gameStage.final,
      ].map((stage) => {
        const list = sortedForStage(stage);
        if (list.length === 0) {
          return null;
        }
        return (
          <Fragment key={`${stage}`}>
            <ListSectionHeader label={convertStageToString(stage)} />
            <VStack align='stretch' spacing={2} pt={2} pb={3}>
              {list.map((fixture, i) => renderFixtureBlock(fixture, `${stage}`, i))}
            </VStack>
          </Fragment>
        );
      })}
    </Accordion>
  );
}
